import { memo, useMemo } from "react";
import { BaseCard } from "./BaseCard";
import { COLORS } from "./constants";
import type { DistributionItem } from "@/lib/utils";

interface DistributionCardProps {
  title: string;
  items: DistributionItem[];
  type: "progress" | "list";
  reportLink: string;
  colors?: string[];
}

export const DistributionCard = memo(({
  title,
  items,
  type,
  reportLink,
  colors = COLORS,
}: DistributionCardProps) => {
  const displayItems = useMemo(() => items.slice(0, 6), [items]);

  if (items.length === 0) {
    return (
      <BaseCard title={title} reportLink={reportLink}>
        <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
          No data
        </div>
      </BaseCard>
    );
  }

  return (
    <BaseCard title={title} reportLink={reportLink}>
      {displayItems.map((item, index) => (
        <div key={item.label} className="text-sm">
          <div className="flex justify-between items-center mb-1">
            <span className="flex items-center gap-2 max-w-[70%]">
              {type === "list" && (
                <span className="w-4 shrink-0 text-muted-foreground tabular-nums">
                  {index + 1}
                </span>
              )}
              <span
                className="block truncate font-medium text-foreground/80"
                title={item.label}
              >
                {item.label}
              </span>
            </span>
            <div className="flex items-center gap-1.5 text-sm shrink-0">
              <span className="font-bold tabular-nums text-foreground">
                {item.count}
              </span>
              <span className="text-xs font-normal text-muted-foreground tabular-nums">
                ({item.percentage}%)
              </span>
            </div>
          </div>
          {type === "progress" && (
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{
                  width: `${item.percentage}%`,
                  backgroundColor: colors[index % colors.length],
                }}
              />
            </div>
          )}
        </div>
      ))}
    </BaseCard>
  );
});

DistributionCard.displayName = "DistributionCard";
